/**
 * Кадр телефона для Laser Dodge: `{type: '…LaserDodgeData', gameState, detectionProgress?, phase?, phaseProgress?,
 * round?, lives?, score?, zones?, roundPassed?, roundPoints?, bodySeen?, joints?, bones?}`.
 * Правила (раунды, касание, жизни) считает телефон — ТВ только рисует то, что пришло. Все координаты — доли экрана
 * телефона (0…1, y вниз, зеркальный кадр камеры, как видит игрок); телефон в ландшафте, поэтому на ТВ они ложатся
 * на весь экран без пересчёта.
 */
export type LaserDodgeGameState = 'PREPARING' | 'STARTED' | 'PAUSED' | 'GAME_OVER';

export type LaserDodgePhase = 'warning' | 'firing' | 'result';

export interface LaserDodgeZone {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

export interface LaserDodgePoint {
  x: number;
  y: number;
}

/** Отрезок скелета: `[x1, y1, x2, y2]`. */
export type LaserDodgeBone = readonly [number, number, number, number];

export interface LaserDodgeFrame {
  gameState: LaserDodgeGameState;
  /** Заполнение обратного отсчёта «встань в силуэт» на телефоне, 0…1; `null` — поля нет. */
  detectionProgress: number | null;
  /** Фаза раунда; только в кадрах `STARTED`. */
  phase: LaserDodgePhase | null;
  /** Доля пройденной фазы, 0…1. */
  phaseProgress: number;
  round: number | null;
  lives: number | null;
  score: number | null;
  /** Красные зоны раунда; пусто — их нет или кадр без них. */
  zones: LaserDodgeZone[];
  /** Итог раунда (фаза `result`); `null` — поля нет. */
  roundPassed: boolean | null;
  roundPoints: number;
  /** `false` — игрока не было в кадре, пока зоны горели (то есть провал без касания). */
  bodySeen: boolean;
  /** Видимые суставы скелета. */
  joints: LaserDodgePoint[];
  /** Кости скелета между видимыми суставами. */
  bones: LaserDodgeBone[];
}

const STATES: readonly unknown[] = ['PREPARING', 'STARTED', 'PAUSED', 'GAME_OVER'];
const PHASES: readonly unknown[] = ['warning', 'firing', 'result'];

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function count(value: unknown): number | null {
  return finite(value) ? Math.max(0, Math.floor(value)) : null;
}

function parseZone(value: unknown): LaserDodgeZone | null {
  if (!value || typeof value !== 'object') return null;
  const {
    left, top, right, bottom,
  } = value as Record<string, unknown>;
  if (!finite(left) || !finite(top) || !finite(right) || !finite(bottom)) return null;
  return {
    left, top, right, bottom,
  };
}

function parsePoint(value: unknown): LaserDodgePoint | null {
  if (!value || typeof value !== 'object') return null;
  const { x, y } = value as Record<string, unknown>;
  return finite(x) && finite(y) ? { x, y } : null;
}

function parseBone(value: unknown): LaserDodgeBone | null {
  if (!Array.isArray(value) || value.length !== 4 || !value.every(finite)) return null;
  return value as unknown as LaserDodgeBone;
}

function list<T>(value: unknown, parse: (item: unknown) => T | null): T[] {
  if (!Array.isArray(value)) return [];
  const out: T[] = [];
  value.forEach((item) => {
    const parsed = parse(item);
    if (parsed) out.push(parsed);
  });
  return out;
}

export function parseLaserDodgeFrame(raw: unknown): LaserDodgeFrame | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Record<string, unknown>;
  if (!STATES.includes(data.gameState)) return null;
  return {
    gameState: data.gameState as LaserDodgeGameState,
    detectionProgress: finite(data.detectionProgress) ? clamp01(data.detectionProgress) : null,
    phase: PHASES.includes(data.phase) ? (data.phase as LaserDodgePhase) : null,
    phaseProgress: finite(data.phaseProgress) ? clamp01(data.phaseProgress) : 0,
    round: count(data.round),
    lives: count(data.lives),
    score: count(data.score),
    zones: list(data.zones, parseZone),
    roundPassed: typeof data.roundPassed === 'boolean' ? data.roundPassed : null,
    roundPoints: count(data.roundPoints) ?? 0,
    bodySeen: data.bodySeen !== false,
    joints: list(data.joints, parsePoint),
    bones: list(data.bones, parseBone),
  };
}
